<template>
    <v-dialog
        :model-value="modelValue"
        max-width="820"
        width="94vw"
        persistent
        @update:model-value="$emit('update:modelValue', $event)">
        <v-card class="stamp-duty-formula-card">
            <v-toolbar density="comfortable" color="surface">
                <v-toolbar-title class="d-flex align-center">
                    Calculator taxă de timbru
                    <span
                        v-if="!readOnly"
                        class="stamp-duty-info ml-2"
                        @click.stop.prevent>
                        <v-icon
                            size="small"
                            color="medium-emphasis"
                            icon="mdi-information-outline" />
                        <v-tooltip
                            activator="parent"
                            location="bottom"
                            max-width="360">
                            {{ calculatorOverviewInfo }}
                        </v-tooltip>
                    </span>
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    v-if="!readOnly"
                    variant="text"
                    prepend-icon="mdi-restore"
                    class="me-1"
                    title="Golește calculatorul. Apoi Aplică pentru a ieși din modul formulă."
                    @click="resetAllToFresh">
                    Resetează
                </v-btn>
                <v-btn icon variant="text" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4 stamp-duty-formula-body">
                <v-row dense class="mb-3">
                    <v-col cols="12" md="6">
                        <div class="stamp-duty-info-block pa-3 rounded border">
                            <div class="text-caption text-medium-emphasis">
                                Greutate din Detalii produs
                            </div>
                            <div class="text-body-1 font-weight-medium mt-1">
                                {{ productWeightDisplay }}
                                <span
                                    v-if="hasProductWeight"
                                    class="text-medium-emphasis font-weight-regular">
                                    kg
                                </span>
                            </div>
                            <div class="text-caption text-medium-emphasis mt-1">
                                {{ productWeightSourceHint }}
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="stamp-duty-info-block pa-3 rounded border">
                            <div class="text-caption text-medium-emphasis">
                                Total taxă (RON)
                            </div>
                            <div class="text-body-1 font-weight-medium mt-1">
                                {{ summary.amountRonDisplay }}
                            </div>
                            <div
                                v-if="summary.amountHint"
                                class="text-caption text-medium-emphasis mt-1">
                                {{ summary.amountHint }}
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <div class="text-subtitle-2 font-weight-medium mb-1">
                    Reguli de calcul
                </div>
                <div class="text-caption text-medium-emphasis mb-3">
                    Alege tipul de calcul pe fiecare linie (pe greutate sau pe bucată).
                    Poți adăuga mai multe reguli — toate intră în total.
                </div>

                <div
                    v-for="(line, index) in draft.lines"
                    :key="line.key"
                    class="mb-3">
                    <div class="pa-3 rounded border">
                        <div class="d-flex align-center justify-space-between ga-2 mb-3">
                            <v-btn-toggle
                                :model-value="line.calcType"
                                class="stamp-duty-type-toggle"
                                density="comfortable"
                                color="primary"
                                variant="outlined"
                                divided
                                mandatory
                                :disabled="readOnly"
                                @update:model-value="onCalcTypeToggle(index, $event)">
                                <v-btn
                                    size="small"
                                    :value="calcPerKg">
                                    Pe greutate
                                </v-btn>
                                <v-btn
                                    size="small"
                                    :value="calcPerUnit">
                                    Pe bucată
                                </v-btn>
                            </v-btn-toggle>
                            <div class="d-flex align-center ga-2 ms-auto">
                                <span class="text-body-2 font-weight-medium text-medium-emphasis">
                                    {{ formatLineAmountRon(line.key) }}
                                </span>
                                <v-btn
                                    icon="mdi-close"
                                    size="small"
                                    variant="text"
                                    :disabled="readOnly || draft.lines.length <= 1"
                                    @click="removeLine(index)" />
                            </div>
                        </div>

                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="line.label"
                                    label="Denumire"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :readonly="readOnly" />
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-text-field
                                    v-model="line.rate"
                                    :label="lineRateLabel(line)"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    inputmode="decimal"
                                    autocomplete="off"
                                    :readonly="readOnly" />
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-select
                                    v-model="line.rateCurrency"
                                    :items="currencyOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Monedă"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :readonly="readOnly" />
                            </v-col>
                            <v-col
                                v-if="line.calcType === calcPerKg"
                                cols="12"
                                md="4">
                                <v-text-field
                                    :model-value="line.weightKg"
                                    label="Greutate"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    inputmode="decimal"
                                    autocomplete="off"
                                    :readonly="readOnly"
                                    :title="lineWeightHint(line)"
                                    @update:model-value="onLineWeightManual(index, $event)">
                                    <template
                                        v-if="!readOnly && line.weightIsManual"
                                        #append-inner>
                                        <v-btn
                                            icon="mdi-refresh"
                                            size="x-small"
                                            variant="text"
                                            title="Revino la greutatea din Detalii produs"
                                            @click.stop="resetLineWeightToProduct(index)" />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col
                                v-else
                                cols="12"
                                md="4">
                                <v-text-field
                                    v-model="line.quantity"
                                    label="Cantitate (buc)"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    inputmode="decimal"
                                    autocomplete="off"
                                    :readonly="readOnly"
                                    title="Implicit 1 bucată" />
                            </v-col>
                        </v-row>
                    </div>

                    <div
                        v-if="!readOnly && index === draft.lines.length - 1"
                        class="d-flex justify-center py-2">
                        <v-btn
                            icon
                            size="x-small"
                            variant="tonal"
                            color="primary"
                            title="Adaugă regulă"
                            @click="addLineAfter(index)">
                            <v-icon size="small">mdi-plus</v-icon>
                        </v-btn>
                    </div>
                </div>

                <div
                    v-if="!readOnly && !draft.lines.length"
                    class="d-flex justify-center py-2">
                    <v-btn
                        variant="tonal"
                        color="primary"
                        prepend-icon="mdi-plus"
                        @click="addLineAfter(-1)">
                        Adaugă regulă
                    </v-btn>
                </div>

                <v-alert
                    v-if="applyBlockedMessage"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mt-3">
                    {{ applyBlockedMessage }}
                </v-alert>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-btn variant="text" @click="close">
                    Anulează
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="!readOnly"
                    color="primary"
                    variant="flat"
                    :disabled="Boolean(applyBlockedMessage)"
                    @click="apply">
                    Aplică
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    applyAutoStampDutyWeight,
    calculateStampDutyBreakdown,
    cloneStampDutyBreakdown,
    createDefaultStampDutyBreakdown,
    createStampDutyLine,
    parseWeightKg,
    STAMP_DUTY_CALC_PER_KG,
    STAMP_DUTY_CALC_PER_UNIT,
    STAMP_DUTY_CURRENCY_OPTIONS,
    STAMP_DUTY_MODE_FORMULA,
    STAMP_DUTY_MODE_MANUAL,
} from '@/utils/stampDutyBreakdown';
import { formatNumericInputValue } from '@/utils/priceListProductCalculator';

const DEFAULT_LABEL_PER_KG = 'Taxă pe greutate';
const DEFAULT_LABEL_PER_UNIT = 'Taxă pe bucată';

function isDefaultStampDutyLabel(label) {
    const normalized = String(label || '').trim().toLowerCase();
    return !normalized
        || normalized === 'taxă de timbru'
        || normalized === 'taxa de timbru'
        || normalized === DEFAULT_LABEL_PER_KG.toLowerCase()
        || normalized === DEFAULT_LABEL_PER_UNIT.toLowerCase()
        || normalized === 'taxă pe kg'
        || normalized === 'taxa pe kg'
        || /^taxă\s+\d+$/i.test(normalized)
        || /^taxa\s+\d+$/i.test(normalized);
}

function defaultLabelForCalcType(calcType) {
    return calcType === STAMP_DUTY_CALC_PER_UNIT
        ? DEFAULT_LABEL_PER_UNIT
        : DEFAULT_LABEL_PER_KG;
}

export default {
    name: 'StampDutyFormulaModal',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        breakdown: {
            type: Object,
            default: () => ({}),
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        productWeight: {
            type: [String, Number],
            default: '',
        },
        pricingSettings: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['update:modelValue', 'apply'],
    data() {
        return {
            draft: createDefaultStampDutyBreakdown(),
            currencyOptions: STAMP_DUTY_CURRENCY_OPTIONS,
            calcPerKg: STAMP_DUTY_CALC_PER_KG,
            calcPerUnit: STAMP_DUTY_CALC_PER_UNIT,
            exitFormulaOnApply: false,
        };
    },
    computed: {
        calculatorOverviewInfo() {
            return [
                'Taxă fixă adăugată la prețul final de achiziție, după costul de transport.',
                'Poți calcula pe greutate sau pe bucată.',
                'Dacă tariful e în altă monedă decât RON, conversia folosește cursul din setările listei.',
            ].join(' ');
        },
        hasProductWeight() {
            return parseWeightKg(this.productWeight) > 0;
        },
        productWeightDisplay() {
            const kg = parseWeightKg(this.productWeight);
            if (kg > 0) {
                return formatNumericInputValue(kg, 4);
            }
            const raw = String(this.productWeight ?? '').trim();
            return raw || '—';
        },
        productWeightSourceHint() {
            if (this.hasProductWeight) {
                return 'Preluată automat pentru regulile pe greutate';
            }
            return 'Nu e completată — introduceți greutatea pe regulă sau în Detalii produs';
        },
        summary() {
            const computed = calculateStampDutyBreakdown(this.draft, {
                weight: this.productWeight,
                pricingSettings: this.pricingSettings,
            });
            return {
                amountRonDisplay: formatNumericInputValue(computed.amountRon, 2) || '0',
                amountRon: computed.amountRon,
                amountHint: 'Se adaugă la prețul de intrare RON (după transport)',
                lineAmounts: computed.lineAmounts,
            };
        },
        applyBlockedMessage() {
            if (this.exitFormulaOnApply) {
                return '';
            }
            const lines = this.draft.lines || [];
            if (!lines.length) {
                return 'Adaugă cel puțin o regulă de calcul.';
            }
            for (const line of lines) {
                if (!String(line.rate ?? '').trim()) {
                    return `Introduceți tariful pentru „${line.label || 'regulă'}”.`;
                }
                if (line.calcType === STAMP_DUTY_CALC_PER_KG) {
                    const weight = String(line.weightKg ?? '').trim();
                    if (!weight && !this.hasProductWeight) {
                        return `Completați greutatea pentru „${line.label || 'regulă'}”.`;
                    }
                }
            }
            return '';
        },
    },
    watch: {
        modelValue(open) {
            if (open) {
                this.hydrateFromProps();
            }
        },
        productWeight() {
            if (!this.modelValue) {
                return;
            }
            this.draft = applyAutoStampDutyWeight(this.draft, {
                weight: this.productWeight,
            });
        },
    },
    methods: {
        hydrateFromProps() {
            this.exitFormulaOnApply = false;
            let next = cloneStampDutyBreakdown(this.breakdown);
            next.mode = STAMP_DUTY_MODE_FORMULA;
            next.amountIsManual = false;
            if (!Array.isArray(next.lines) || !next.lines.length) {
                next.lines = [createStampDutyLine({
                    label: DEFAULT_LABEL_PER_KG,
                    enabled: true,
                    calcType: STAMP_DUTY_CALC_PER_KG,
                })];
            } else {
                next.lines = next.lines.map((line) => createStampDutyLine({
                    ...line,
                    enabled: true,
                }));
            }
            next = applyAutoStampDutyWeight(next, { weight: this.productWeight });
            this.draft = next;
        },
        lineRateLabel(line) {
            return line.calcType === STAMP_DUTY_CALC_PER_UNIT
                ? 'Tarif / buc'
                : 'Tarif';
        },
        lineWeightHint(line) {
            if (line.weightIsManual) {
                return 'Valoare manuală — refresh pentru Detalii produs';
            }
            if (this.hasProductWeight) {
                return 'Preluată din Detalii produs';
            }
            return 'Introduceți greutatea manual';
        },
        formatLineAmountRon(lineKey) {
            const row = (this.summary.lineAmounts || []).find((line) => line.key === lineKey);
            if (!row) {
                return '';
            }
            const amount = formatNumericInputValue(row.amountRon, 2) || '0';
            return `= ${amount} RON`;
        },
        onCalcTypeToggle(index, calcType) {
            const line = this.draft.lines[index];
            if (!line || !calcType) {
                return;
            }
            const previousType = line.calcType;
            line.calcType = calcType === STAMP_DUTY_CALC_PER_UNIT
                ? STAMP_DUTY_CALC_PER_UNIT
                : STAMP_DUTY_CALC_PER_KG;
            if (
                previousType !== line.calcType
                && isDefaultStampDutyLabel(line.label)
            ) {
                line.label = defaultLabelForCalcType(line.calcType);
            }
            if (line.calcType === STAMP_DUTY_CALC_PER_KG) {
                line.weightIsManual = false;
                this.draft = applyAutoStampDutyWeight(this.draft, {
                    weight: this.productWeight,
                });
            }
        },
        onLineWeightManual(index, value) {
            const line = this.draft.lines[index];
            if (!line) {
                return;
            }
            line.weightKg = value;
            line.weightIsManual = true;
        },
        resetLineWeightToProduct(index) {
            const line = this.draft.lines[index];
            if (!line) {
                return;
            }
            line.weightIsManual = false;
            this.draft = applyAutoStampDutyWeight(this.draft, {
                weight: this.productWeight,
            });
        },
        addLineAfter(index) {
            const calcType = STAMP_DUTY_CALC_PER_KG;
            const line = createStampDutyLine({
                label: defaultLabelForCalcType(calcType),
                enabled: true,
                calcType,
            });
            const lines = [...this.draft.lines];
            lines.splice(index + 1, 0, line);
            this.draft.lines = lines;
            this.draft = applyAutoStampDutyWeight(this.draft, {
                weight: this.productWeight,
            });
        },
        removeLine(index) {
            if (this.draft.lines.length <= 1) {
                return;
            }
            this.draft.lines.splice(index, 1);
        },
        resetAllToFresh() {
            this.exitFormulaOnApply = true;
            this.draft = createDefaultStampDutyBreakdown({
                mode: STAMP_DUTY_MODE_MANUAL,
                amountIsManual: true,
                lines: [createStampDutyLine({
                    label: DEFAULT_LABEL_PER_KG,
                    enabled: true,
                    calcType: STAMP_DUTY_CALC_PER_KG,
                })],
            });
        },
        close() {
            this.$emit('update:modelValue', false);
        },
        apply() {
            if (this.applyBlockedMessage) {
                return;
            }
            if (this.exitFormulaOnApply) {
                this.$emit('apply', createDefaultStampDutyBreakdown({
                    mode: STAMP_DUTY_MODE_MANUAL,
                    amountIsManual: true,
                }));
                this.close();
                return;
            }
            let payload = cloneStampDutyBreakdown(this.draft);
            payload = applyAutoStampDutyWeight(payload, {
                weight: this.productWeight,
            });
            payload.mode = STAMP_DUTY_MODE_FORMULA;
            payload.amountIsManual = false;
            // Every visible line is included in the total.
            payload.lines = (payload.lines || []).map((line) => ({
                ...line,
                enabled: true,
            }));
            this.$emit('apply', payload);
            this.close();
        },
    },
};
</script>

<style scoped>
.stamp-duty-formula-card {
    display: flex;
    flex-direction: column;
    max-height: min(92vh, 820px);
}

.stamp-duty-formula-body {
    overflow-y: auto;
    flex: 1 1 auto;
}

.stamp-duty-info {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 1;
    cursor: help;
}

.stamp-duty-type-toggle {
    flex-shrink: 0;
}

.stamp-duty-info-block {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
    border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
    min-height: 100%;
}
</style>
